import React, { useMemo, useState } from 'react';
import { Box, TextField, Typography, Autocomplete, CircularProgress } from '@mui/material';
import axios from 'axios';

function placeLabel(place) {
  return [place.name, place.admin1, place.country].filter(Boolean).join(', ');
}

/**
 * Selector de ubicación para el widget de clima: buscador de lugares por nombre en
 * cualquier país (clientes en Chile, México, Perú, Argentina...) que autocompleta
 * nombre/latitud/longitud, más esos mismos campos siempre visibles y editables a
 * mano debajo (por si el lugar exacto no aparece en la búsqueda, ej. un estadio
 * puntual, o para ajustar el nombre). Componente controlado: recibe label/lat/lon
 * y reporta cambios via onChange, para reutilizarse al agregar y al editar.
 */
const WeatherLocationPicker = ({ label, lat, lon, onChange, getAuthHeaders }) => {
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchPlaces = useMemo(() => {
    let timeoutId;
    return (query) => {
      clearTimeout(timeoutId);
      if (!query || query.trim().length < 2) {
        setOptions([]);
        return;
      }
      timeoutId = setTimeout(async () => {
        setSearching(true);
        try {
          const res = await axios.get('/api/weather/search', {
            params: { q: query.trim() },
            headers: getAuthHeaders?.(),
          });
          setOptions(res.data?.places || []);
        } catch {
          setOptions([]);
        } finally {
          setSearching(false);
        }
      }, 350);
    };
  }, [getAuthHeaders]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Autocomplete
        options={options}
        getOptionLabel={placeLabel}
        filterOptions={(x) => x}
        value={null}
        loading={searching}
        noOptionsText="Escribe al menos 2 caracteres para buscar"
        onInputChange={(e, value) => searchPlaces(value)}
        onChange={(e, place) => {
          if (place) onChange({ label: `Tiempo ${place.name}`, lat: String(place.lat), lon: String(place.lon) });
        }}
        sx={{ mt: 0.75 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Buscar ciudad o lugar"
            size="small"
            placeholder="Ej. Ciudad de México, Lima, Buenos Aires..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searching ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Typography variant="caption" color="text.secondary">
        O completa/ajusta los datos directamente:
      </Typography>
      <TextField
        label="Nombre"
        size="small"
        value={label}
        onChange={(e) => onChange({ label: e.target.value, lat, lon })}
      />
      <TextField
        label="Latitud"
        size="small"
        value={lat}
        onChange={(e) => onChange({ label, lat: e.target.value, lon })}
      />
      <TextField
        label="Longitud"
        size="small"
        value={lon}
        onChange={(e) => onChange({ label, lat, lon: e.target.value })}
      />
    </Box>
  );
};

export default WeatherLocationPicker;
